import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import productService from '../../services/productService';

const BestSeller = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        setLoading(true);
        // Fetch trending products as best sellers, or fetch all products and take first 5
        const response = await productService.getHomepageData();
        if (response.success) {
          // Use trending products if available, otherwise use new arrivals, otherwise use all products
          let bestSellers = [];
          if (response.trendingProducts && response.trendingProducts.length > 0) {
            bestSellers = response.trendingProducts.slice(0, 5);
          } else if (response.newArrivals && response.newArrivals.length > 0) {
            bestSellers = response.newArrivals.slice(0, 5);
          } else {
            // Fallback: fetch all products
            const productsResponse = await productService.getProducts();
            if (productsResponse.success) {
              bestSellers = productsResponse.products.slice(0, 5);
            }
          }
          setProducts(bestSellers);
        } else {
          // Fallback: fetch all products
          const productsResponse = await productService.getProducts();
          if (productsResponse.success) {
            setProducts(productsResponse.products.slice(0, 5));
          } else {
            setError('Failed to load products');
          }
        }
      } catch (err) {
        setError('Error loading products');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBestSellers();
  }, []);

  // Helper function to convert color names to hex codes
  const getColorHex = (colorName) => {
    const colorMap = {
      'black': '#000000',
      'charcoal': '#36454F',
      'white': '#FFFFFF',
      'red': '#DC2626',
      'blue': '#1E3A8A',
      'royal': '#4169E1',
      'yellow': '#FCD34D',
      'green': '#10B981',
      'military green': '#4B5320',
      'gray': '#6B7280',
      'grey': '#6B7280',
      'purple': '#8B5CF6',
      'brown': '#7F1D1D',
      'orange': '#F59E0B',
      'navy': '#000080',
      'sand': '#C2B280',
      'maroon': '#800000',
      'lightgray': '#D4D4D4',
      'lightgrey': '#D4D4D4',
    };
    return colorMap[colorName?.toLowerCase()] || '#000000';
  };

  // Helper function to parse colors from product
  const parseColors = (product) => {
    // Handle both 'color' (singular) and 'colors' (plural) for backward compatibility
    let colors = product.color || product.colors || [];
    if (typeof colors === 'string') {
      // If it's a comma-separated string, split it
      colors = colors.split(',').map(c => c.trim()).filter(c => c.length > 0);
    }
    if (!Array.isArray(colors)) {
      colors = [];
    }
    return colors;
  };

  // Skeleton loader component for a single product card
  const ProductCardSkeleton = () => (
    <div className="group animate-pulse">
      <div className="relative mb-4 overflow-hidden rounded-lg">
        <div className="w-full h-[400px] bg-gray-200"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="flex items-center space-x-1 mb-2">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="w-4 h-4 rounded-full bg-gray-200 border border-gray-300"></div>
        ))}
      </div>
      <div className="h-6 bg-gray-200 rounded w-24"></div>
    </div>
  );

  // Skeleton loader component for bottom product card (with sale badge)
  const BottomProductCardSkeleton = ({ showSale = false }) => (
    <div className="group animate-pulse">
      <div className="relative mb-4 overflow-hidden rounded-lg">
        <div className="w-full h-[400px] bg-gray-200"></div>
        {showSale && (
          <div className="absolute top-4 right-4 w-20 h-6 bg-gray-300 rounded"></div>
        )}
      </div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="flex items-center space-x-1 mb-2">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="w-4 h-4 rounded-full bg-gray-200 border border-gray-300"></div>
        ))}
      </div>
      <div className="h-6 bg-gray-200 rounded w-24"></div>
    </div>
  );

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-2">Best Seller</h2>
          <p className="text-gray-600">Fresh Styles, Fresh Vibes: Upgrade your wardrobe with our latest</p>
        </div>

        {/* Top row skeleton - 3 products */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <ProductCardSkeleton />
          <ProductCardSkeleton />
          <ProductCardSkeleton />
        </div>

        {/* Bottom row skeleton - 2 products */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <BottomProductCardSkeleton showSale={true} />
          <BottomProductCardSkeleton />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-2">Best Seller</h2>
          <p className="text-gray-600">Fresh Styles, Fresh Vibes: Upgrade your wardrobe with our latest</p>
        </div>
        <div className="flex justify-center items-center h-[400px]">
          <div className="text-red-600">{error}</div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-2">Best Seller</h2>
          <p className="text-gray-600">Fresh Styles, Fresh Vibes: Upgrade your wardrobe with our latest</p>
        </div>
        <div className="flex justify-center items-center h-[400px]">
          <div className="text-gray-500">No products available</div>
        </div>
      </section>
    );
  }

  // Split products: first 3 for top row, next 2 for bottom row
  const topProducts = products.slice(0, 3);
  const bottomProducts = products.slice(3, 5);

  return (
    <section className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-2">Best Seller</h2>
        <p className="text-gray-600">Fresh Styles, Fresh Vibes: Upgrade your wardrobe with our latest</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {topProducts.map((product) => (
          <Link
            key={product._id}
            to={`/product/${product._id}`}
            className="group"
          >
            <div className="relative mb-4 overflow-hidden rounded-lg">
              <img
                src={
                  product.images && product.images[0]
                    ? product.images[0].path
                    : "https://images.pexels.com/photos/1035685/pexels-photo-1035685.jpeg?auto=compress&cs=tinysrgb&w=400"
                }
                alt={product.productTitle}
                className="w-full h-[400px] object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <h3 className="text-sm mb-2 line-clamp-2">{product.productTitle}</h3>
            <div className="flex items-center space-x-1 mb-2">
              {(() => {
                const colors = parseColors(product);
                return colors.length > 0 ? (
                  colors.slice(0, 7).map((color, i) => (
                    <span
                      key={i}
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: getColorHex(color) }}
                    ></span>
                  ))
                ) : (
                  <span className="w-4 h-4 rounded-full border border-gray-300 bg-gray-300"></span>
                );
              })()}
            </div>
            <p className="font-semibold text-lg">
              {user && product.msrp 
                ? `$${parseFloat(product.msrp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'Login to View Price'}
            </p>
          </Link>
        ))}
      </div>

      {bottomProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {bottomProducts.map((product, index) => (
            <Link
              key={product._id}
              to={`/product/${product._id}`}
              className="group"
            >
              <div className="relative mb-4 overflow-hidden rounded-lg">
                <img
                  src={
                    product.images && product.images[0]
                      ? product.images[0].path
                      : "https://images.pexels.com/photos/1035685/pexels-photo-1035685.jpeg?auto=compress&cs=tinysrgb&w=400"
                  }
                  alt={product.productTitle}
                  className="w-full h-[400px] object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {index === 0 && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded text-sm font-medium">
                    Sale 20%
                  </div>
                )}
              </div>
              <h3 className="text-sm mb-2 line-clamp-2">{product.productTitle}</h3>
              <p className="text-xs text-gray-500 mb-2">Plain - Logo for Your Price</p>
              <div className="flex items-center space-x-1 mb-2">
                {(() => {
                  const colors = parseColors(product);
                  return colors.length > 0 ? (
                    colors.slice(0, 7).map((color, i) => (
                      <span
                        key={i}
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: getColorHex(color) }}
                      ></span>
                    ))
                  ) : (
                    <span className="w-4 h-4 rounded-full border border-gray-300 bg-gray-300"></span>
                  );
                })()}
              </div>
              <p className="font-semibold text-lg">
                {user && product.msrp 
                  ? `$${parseFloat(product.msrp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : 'Login to View Price'}
              </p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default BestSeller;
